import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { inRange } from '@sama/micro-dash';
import { DirectiveSuperclass } from '@sama/ng-core';
import { BehaviorSubject, combineLatest, filter, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ShapeState } from '../../../../../../cross-team-shared/annotation/shared/state/shape-state';
import {
  isDynamicKeyframe,
  isKeyLocation,
} from '../../../../../../cross-team-shared/annotation/shared/utils/keyframe-utils';
import { AbstractAnnotationStore } from '../../../../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import { VisibilityState } from '../../../../../../team-apis/otterdam/answers/shape-typings';
import { outputInfoStoreToken } from '../../../../../h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../../../../h3ml/state/task-workspace-store';
import { TimelineContextMenuService } from '../../../../../shared/context-menu/timeline-context-menu.service';
import { ShapeFeedbackDialogService } from '../../../../../shared/feedback/shape-feedback-dialog.service';
import { FusionManager } from '../../fusion/fusion-manager';
import { FocusedAnnotationService } from '../../utils/focused-annotation.service';
import { ObjectlessFocusedShapeService } from '../../utils/objectless-focused-shape.service';
import { RootFocusedAnnotationService } from '../../utils/root-focused-annotation.service';
import { RootFocusedShapeService } from '../../utils/root-focused-shape.service';

@Component({
  selector: 'h3-frame',
  templateUrl: './frame.component.html',
  styleUrls: [
    './frame.component.scss',
    '../../../../../../outputs/annotation/annotation-shared/visibility.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FrameComponent extends DirectiveSuperclass implements OnInit {
  @Input({ required: true }) frameNumber!: number;
  visibility$!: Observable<VisibilityState>;
  isObject$!: Observable<boolean>;
  isKeyLocation$!: Observable<boolean>;
  isDynamicKeyFrame$!: Observable<boolean>;
  isMlFrame$!: Observable<boolean>;
  isMlPendingFrame$!: Observable<boolean>;
  hasFeedback$!: Observable<boolean>;
  isKeyLocationOnly$ = new BehaviorSubject(false);
  isDynamicKeyFrameOnly$ = new BehaviorSubject(false);
  isDynamicKeyLocation$ = new BehaviorSubject(false);
  isFrameDisplayedInRuler$!: Observable<boolean>;
  isCurrentFrame$!: Observable<boolean>;
  isInRange$!: Observable<boolean>;

  private fusionManager = inject(FusionManager);
  private outputInfoStore = inject(outputInfoStoreToken);
  private output = this.outputInfoStore.state().output;
  private store = inject(AbstractAnnotationStore);
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private shapeFeedbackDialogService = inject(ShapeFeedbackDialogService);
  private contextMenuService = inject(TimelineContextMenuService);
  private focusedAnnotationService = this.taskWorkspaceStore.isUsingObjects()
    ? inject(RootFocusedAnnotationService)
    : inject(FocusedAnnotationService);

  private focusedShapeService = this.taskWorkspaceStore.isUsingObjects()
    ? inject(RootFocusedShapeService)
    : inject(ObjectlessFocusedShapeService);

  @HostListener('contextmenu', ['$event'])
  openContextMenu(event: MouseEvent) {
    if (
      this.taskWorkspaceStore.state().feedbackType === 'shape' &&
      this.shapeFeedbackDialogService.isEditable()
    ) {
      event.preventDefault();
      this.contextMenuService.open([event.x, event.y]);
    }
  }

  @HostListener('click', ['$event'])
  selectFrame(event: MouseEvent) {
    if (
      event.shiftKey &&
      this.taskWorkspaceStore.state().feedbackType === 'shape' &&
      this.shapeFeedbackDialogService.isEditable()
    ) {
      const framesRange = [this.store.state().frameNumber, this.frameNumber];
      this.shapeFeedbackDialogService.setSelectedFrameRange(
        Math.min(...framesRange),
        Math.max(...framesRange),
      );
    } else {
      this.fusionManager.scrubTo(this.frameNumber);
    }
  }

  ngOnInit() {
    this.isKeyLocation$ = this.buildIsKeyLocation$();
    this.isDynamicKeyFrame$ = this.buildIsDynamicFrame$();
    this.visibility$ = this.buildVisibility$();
    this.isMlFrame$ = this.buildIsMlFrame$();
    this.isObject$ = this.buildIsObject$();
    this.isMlPendingFrame$ = this.buildIsMlPendingFrame$();
    this.isFrameDisplayedInRuler$ = this.buildIsFrameDisplayedInRuler$();
    this.subscribeTo(
      combineLatest([this.isKeyLocation$, this.isDynamicKeyFrame$]),
      ([isAKeyLocation, isDynamicKeyFrame]) => {
        this.isKeyLocationOnly$.next(isAKeyLocation && !isDynamicKeyFrame);
        this.isDynamicKeyFrameOnly$.next(!isAKeyLocation && isDynamicKeyFrame);
        this.isDynamicKeyLocation$.next(isAKeyLocation && isDynamicKeyFrame);
      },
    );
    this.hasFeedback$ = this.buildHasFeedback$();

    this.setUpSubscriptions();
  }

  private setUpSubscriptions() {
    this.isCurrentFrame$ = this.fusionManager
      .getFrameNumberForOutput$(this.output.name)
      .pipe(
        map((selectedFrameNumber) => selectedFrameNumber === this.frameNumber),
      );
    this.isInRange$ = this.taskWorkspaceStore('feedbackPopover')(
      'selectedFrameRange',
    ).$.pipe(
      map((frameRange) =>
        inRange(this.frameNumber, frameRange[0], frameRange[1] + 1),
      ),
    );
  }

  private buildIsKeyLocation$() {
    return this.focusedAnnotationService.getLocations$().pipe(
      filter(isDefined),
      map((annotationStore) =>
        isKeyLocation(annotationStore.key_locations, this.frameNumber),
      ),
    );
  }

  private buildIsDynamicFrame$() {
    return this.focusedAnnotationService.getFocusedTags$().pipe(
      map(
        (tags) =>
          isDefined(tags) &&
          // @ts-ignore -- fix me
          isDynamicKeyframe(tags.dynamic_tags, this.frameNumber),
      ),
    );
  }

  private buildVisibility$() {
    return this.focusedAnnotationService.getVisibility$(this.frameNumber);
  }

  private buildIsObject$() {
    return this.focusedAnnotationService
      .getAnnotationStores$()
      .pipe(switchMap(([_, objectStore]) => of(!!objectStore)));
  }

  private buildIsMlFrame$() {
    return this.getShapeStore$(false, (shapeStore) =>
      shapeStore('key_locations').$.pipe(
        filter(isDefined),
        map((keyLocations) => {
          const closestKeyLocationWithMlIndex = keyLocations.findLastIndex(
            (keyLocation) =>
              keyLocation.frame_number <= this.frameNumber &&
              !!keyLocation.extrapolation?.points,
          );
          return (
            closestKeyLocationWithMlIndex >= 0 &&
            keyLocations[closestKeyLocationWithMlIndex].frame_number +
              keyLocations[closestKeyLocationWithMlIndex].extrapolation!.points
                .length >=
              this.frameNumber
          );
        }),
      ),
    );
  }

  private buildIsMlPendingFrame$() {
    return this.getShapeStore$(false, (shapeStore) =>
      this.store('inFlightMlExtrapolationRequests').$.pipe(
        filter(isDefined), // edge case when output component gets destroyed
        map(
          (requests) =>
            !!requests.filter((request) => {
              // fix me if you change this code
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
              if (request.shapeId === shapeStore.state()?.index) {
                return (
                  request.keyFrameNumber +
                    request.requestedExtrapolatedFramesCount >=
                    this.frameNumber &&
                  this.frameNumber >= request.keyFrameNumber
                );
              }
              return false;
            }).length,
        ),
      ),
    );
  }

  private buildIsFrameDisplayedInRuler$() {
    return this.store('focusedAreaRange').$.pipe(
      map(() => this.frameNumber % 5 === 0),
    );
  }

  private buildHasFeedback$() {
    return this.getShapeStore$(false, (shapeStore) =>
      this.taskWorkspaceStore.getShapeFeedback$(shapeStore.state().index).pipe(
        map((shapeFeedback) =>
          shapeFeedback.some((feedback) => {
            const startFrame = feedback.startFrame ?? 0;
            const endFrame =
              feedback.endFrame ??
              this.taskWorkspaceStore.getMaxFrameNumber(this.output.name);

            return (
              this.frameNumber >= startFrame && this.frameNumber <= endFrame
            );
          }),
        ),
      ),
    );
  }

  private getShapeStore$<T>(
    defaultTo: T,
    switchMapCallback: (shapeStore: Store<ShapeState>) => Observable<T>,
  ) {
    return this.focusedShapeService
      .getFocusedShapeStore$()
      .pipe(
        switchMap((store) =>
          !store ? of(defaultTo) : switchMapCallback(store),
        ),
      );
  }
}
